import type {
  ApprovalRequest,
  ApprovalRequestStatus,
  FeePaymentDetail,
  PaymentActionResponse,
  PaymentAdjustment,
} from "@wallyo/contracts/console/payments";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import type { ConsoleFile } from "../../api/client";
import { exportPayments, getPayment, listApprovalRequests, listPayments } from "../../api/console-api";
import { useApi } from "../../api/context";
import { ConsoleApiError } from "../../api/errors";
import { createIntent } from "../../api/idempotency";
import { t } from "../../i18n";
import { formatMoney } from "../../i18n/format";
import { useHash } from "../../navigation/use-hash";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { ApprovalsView, type ApprovalsState } from "./ApprovalsView";
import { INITIAL_PAYMENT_LIST, paymentListReducer } from "./list-state";
import {
  EMPTY_PAYMENT_FIELDS,
  PAYMENT_FEATURES,
  paymentActionProblem,
  sendApprovalDecision,
  sendPaymentAction,
  sendSettle,
  type ApprovalActionKind,
  type PaymentActionKind,
} from "./payment-actions";
import { describePaymentFailure, outcomeMessage } from "./payment-format";
import { parsePaymentsHash, paymentsHref, paymentsRequest, type PaymentFilters } from "./payment-route";
import { PaymentActionDialog, type PaymentDialogState } from "./PaymentActionDialog";
import { PaymentDetailView, type PaymentOutcome } from "./PaymentDetailView";
import { PaymentListView } from "./PaymentListView";

/** What a dialog acts on: the payment on screen, one of its refunds, or an approval request. */
type Target =
  | { type: "payment"; kind: PaymentActionKind }
  | { type: "adjustment"; adjustment: PaymentAdjustment }
  | { type: "approval"; request: ApprovalRequest; kind: ApprovalActionKind };

/**
 * Paiements (VIEW_PAYMENTS, checked by the shell). `#payments` lists fee
 * payments, `#payments?approvals=pending` the approval requests, and
 * `#payments/<id>` opens one payment beside the list. Money is never
 * optimistic: every write carries its intent's Idempotency-Key and the version
 * on screen, and the screen shows only what the server answered.
 */
export function PaymentsScreen() {
  const hash = useHash();
  const { client } = useApi();
  const { me } = useStaff();
  const { filters, approvals, platformFeePaymentId } = parsePaymentsHash(hash);
  const has = (feature: (typeof PAYMENT_FEATURES)[keyof typeof PAYMENT_FEATURES]) => me.features.includes(feature);

  const [list, dispatch] = useReducer(paymentListReducer, INITIAL_PAYMENT_LIST);
  const [listReads, readList] = useReducer((count: number) => count + 1, 0);
  const [requests, setRequests] = useState<ApprovalsState>({ status: "loading", requests: [] });
  const [requestReads, readRequests] = useReducer((count: number) => count + 1, 0);
  const generation = useRef(0);
  const shown = useRef<string | null>(null);
  const filtersKey = paymentsHref(filters);

  useEffect(() => {
    if (approvals) return;
    const mine = ++generation.current;
    dispatch({ type: "load", keep: shown.current === filtersKey });
    shown.current = filtersKey;
    listPayments(client, paymentsRequest(parsePaymentsHash(filtersKey).filters)).then(
      (page) => generation.current === mine && dispatch({ type: "loaded", page }),
      (error: unknown) => generation.current === mine && dispatch({ type: "failed", error }),
    );
  }, [client, filtersKey, listReads, approvals]);

  useEffect(() => {
    if (!approvals) return;
    let current = true;
    setRequests((was) => ({ status: "loading", requests: was.requests }));
    listApprovalRequests(client, { status: approvals }).then(
      ({ approvalRequests }) => current && setRequests({ status: "ready", requests: approvalRequests }),
      (error: unknown) => current && setRequests((was) => ({ status: "error", requests: was.requests, error })),
    );
    return () => {
      current = false;
    };
  }, [client, approvals, requestReads]);

  const loadMore = useCallback(() => {
    if (!list.nextCursor || list.loadingMore) return;
    const mine = generation.current;
    dispatch({ type: "loadMore" });
    listPayments(client, paymentsRequest(parsePaymentsHash(filtersKey).filters, list.nextCursor)).then(
      (page) => generation.current === mine && dispatch({ type: "loadedMore", page }),
      (error: unknown) => generation.current === mine && dispatch({ type: "moreFailed", error }),
    );
  }, [client, filtersKey, list.nextCursor, list.loadingMore]);

  const onFilters = useCallback(
    (next: PaymentFilters) => {
      window.location.hash = paymentsHref(next, platformFeePaymentId);
    },
    [platformFeePaymentId],
  );

  const changed = useCallback(() => {
    readList();
    readRequests();
  }, []);

  // A decision taken from the approvals list, with no payment open.
  const [approvalDialog, setApprovalDialog] = useState<Target | null>(null);
  const [approvalOutcome, setApprovalOutcome] = useState<PaymentOutcome | null>(null);

  return (
    <>
      <PageTop title={t("screen.payments")} sub={t("sub.payments")} />
      <div className="tabs payment-tabs">
        <a className={`tab${approvals ? "" : " on"}`} href={paymentsHref(filters, platformFeePaymentId)}>
          {t("payments.tab.payments")}
        </a>
        <a
          className={`tab${approvals ? " on" : ""}`}
          href={paymentsHref(filters, platformFeePaymentId, approvals ?? "pending")}
        >
          {t("payments.tab.approvals")}
        </a>
      </div>
      {has(PAYMENT_FEATURES.export) ? <ExportPanel /> : null}
      {approvalOutcome ? (
        <div className={`banner ${approvalOutcome.tone === "ok" ? "ok" : "bad"}`} role="status" data-outcome="">
          <span>{approvalOutcome.text}</span>
          <button className="btn quiet small" type="button" onClick={() => setApprovalOutcome(null)}>
            {t("reports.dismiss")}
          </button>
        </div>
      ) : null}
      <div className="split list">
        {approvals ? (
          <ApprovalsView
            status={approvals}
            state={requests}
            me={{ staffMemberId: me.staffMember.staffMemberId, features: me.features }}
            onRetry={readRequests}
            onDecide={(request, kind) => {
              setApprovalOutcome(null);
              setApprovalDialog({ type: "approval", request, kind });
            }}
          />
        ) : (
          <PaymentListView
            filters={filters}
            state={list}
            selectedId={platformFeePaymentId}
            onFilters={onFilters}
            onRetry={readList}
            onLoadMore={loadMore}
          />
        )}
        {platformFeePaymentId ? (
          <PaymentPanel
            key={platformFeePaymentId}
            platformFeePaymentId={platformFeePaymentId}
            filters={filters}
            approvals={approvals}
            onChanged={changed}
          />
        ) : (
          <div className="card">
            <p className="foot">{t("payments.pick")}</p>
          </div>
        )}
      </div>
      {approvalDialog ? (
        <ActionDialog
          target={approvalDialog}
          payment={null}
          onClose={() => setApprovalDialog(null)}
          onReload={() => {
            setApprovalDialog(null);
            changed();
          }}
          onDone={(response) => {
            setApprovalDialog(null);
            setApprovalOutcome(outcomeMessage(response));
            changed();
          }}
        />
      ) : null}
    </>
  );
}

type PanelState =
  | { status: "loading"; payment: FeePaymentDetail | null }
  | { status: "ready"; payment: FeePaymentDetail }
  | { status: "failed"; payment: FeePaymentDetail | null; error: unknown };

function PaymentPanel({
  platformFeePaymentId,
  filters,
  approvals,
  onChanged,
}: {
  platformFeePaymentId: string;
  filters: PaymentFilters;
  approvals: ApprovalRequestStatus | null;
  onChanged: () => void;
}) {
  const { client } = useApi();
  const { me } = useStaff();
  const [state, setState] = useState<PanelState>({ status: "loading", payment: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [target, setTarget] = useState<Target | null>(null);
  const [outcome, setOutcome] = useState<PaymentOutcome | null>(null);

  useEffect(() => {
    let current = true;
    setState((was) => ({ status: "loading", payment: was.payment }));
    getPayment(client, platformFeePaymentId).then(
      ({ payment }) => current && setState({ status: "ready", payment }),
      (error: unknown) => current && setState((was) => ({ status: "failed", payment: was.payment, error })),
    );
    return () => {
      current = false;
    };
  }, [client, platformFeePaymentId, reads]);

  const payment = state.payment;
  if (!payment) {
    return (
      <div className="card">
        {state.status === "failed" ? (
          <div className="audit-error">
            <p className="foot">
              {state.error instanceof ConsoleApiError && state.error.status === 404
                ? t("payments.notFound")
                : t("payments.loadFailed")}
            </p>
            <ErrorNotice error={state.error} />
            <div>
              <button className="btn primary" type="button" onClick={read}>
                {t("audit.retry")}
              </button>
            </div>
          </div>
        ) : (
          <div className="empty" role="status">
            <span className="spinner" aria-hidden="true" />
            <span>{t("payments.loading")}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="case-column" data-approvals={approvals ?? undefined}>
      {state.status === "failed" ? (
        <div className="banner bad" role="alert">
          <span>{t("payments.refreshFailed")}</span>
          <button className="btn" type="button" onClick={read}>
            {t("audit.retry")}
          </button>
        </div>
      ) : null}
      <PaymentDetailView
        payment={payment}
        filters={filters}
        features={me.features}
        outcome={outcome}
        onAction={(kind) => {
          setOutcome(null);
          setTarget({ type: "payment", kind });
        }}
        onSettle={(adjustment) => {
          setOutcome(null);
          setTarget({ type: "adjustment", adjustment });
        }}
        onDismissOutcome={() => setOutcome(null)}
      />
      {target ? (
        <ActionDialog
          target={target}
          payment={payment}
          onClose={() => setTarget(null)}
          onReload={() => {
            setTarget(null);
            read();
            onChanged();
          }}
          onDone={(response) => {
            setTarget(null);
            setOutcome(outcomeMessage(response));
            // The payment as the server answered it; read again when the answer has none.
            if (response.payment) setState({ status: "ready", payment: response.payment });
            else read();
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The dialog for one money write. One intent, one Idempotency-Key: kept
 * across "try again" after a failure or a lost answer, and new once anything
 * is edited or the write succeeded.
 */
function ActionDialog({
  target,
  payment,
  onClose,
  onReload,
  onDone,
}: {
  target: Target;
  payment: FeePaymentDetail | null;
  onClose: () => void;
  onReload: () => void;
  onDone: (response: PaymentActionResponse) => void;
}) {
  const { client } = useApi();
  const kind = target.type === "payment" ? target.kind : target.type === "adjustment" ? "settle" : target.kind;
  const [dialog, setDialog] = useState<PaymentDialogState>({
    kind,
    fields: EMPTY_PAYMENT_FIELDS,
    problem: null,
    failure: null,
    pending: false,
  });
  const intent = useRef(createIntent());
  const inFlight = useRef(false);

  const subject =
    target.type === "approval"
      ? target.request.requestedBy.name
      : target.type === "adjustment"
        ? formatMoney(target.adjustment.amount, target.adjustment.currency)
        : payment
          ? `${payment.pro.name} · ${formatMoney(payment.amount, payment.currency)}`
          : "";

  const submit = async () => {
    if (inFlight.current) return;
    const problem = paymentActionProblem(kind, dialog.fields, payment);
    if (problem) {
      setDialog((was) => ({ ...was, problem }));
      return;
    }
    inFlight.current = true;
    setDialog((was) => ({ ...was, problem: null, failure: null, pending: true }));
    const key = intent.current.key();
    try {
      const response =
        target.type === "approval"
          ? await sendApprovalDecision(client, target.request, target.kind, dialog.fields.reason, key)
          : target.type === "adjustment"
            ? await sendSettle(client, target.adjustment, dialog.fields, key)
            : payment
              ? await sendPaymentAction(client, payment, target.kind, dialog.fields, key)
              : null;
      intent.current.reset();
      if (response) onDone(response);
    } catch (error) {
      setDialog((was) => ({
        ...was,
        pending: false,
        failure: describePaymentFailure(error, target.type === "approval" ? "approval" : "general"),
      }));
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <PaymentActionDialog
      dialog={dialog}
      payment={payment}
      subject={subject}
      provider={payment?.provider ?? (target.type === "adjustment" ? target.adjustment.provider : null)}
      onFields={(fields) => {
        intent.current.reset();
        setDialog((was) => ({ ...was, fields: { ...was.fields, ...fields }, problem: null }));
      }}
      onSubmit={() => void submit()}
      onClose={onClose}
      onReload={onReload}
    />
  );
}

/** "2026-09": the month the export defaults to. */
export function currentMonth(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Hands the browser a downloaded file to save. */
function saveFile(file: ConsoleFile, fallbackName: string): void {
  const blob = new Blob([file.text], { type: file.contentType ?? "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename ?? fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** The month's CSV for reconciliation (EXPORT_PAYMENTS). Audited by the server as a sensitive read. */
function ExportPanel() {
  const { client } = useApi();
  const [month, setMonth] = useState(() => currentMonth());
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  const run = async () => {
    if (pending || !/^\d{4}-\d{2}$/.test(month)) return;
    setPending(true);
    setError(null);
    setDone(false);
    try {
      saveFile(await exportPayments(client, month, reason), `walliyo-payments-${month}.csv`);
      setDone(true);
    } catch (failure) {
      setError(failure);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="card payment-export">
      <div className="form queue-filters">
        <div className="field">
          <label htmlFor="payment-export-month">{t("payments.export.month")}</label>
          <input
            id="payment-export-month"
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="payment-export-reason">{t("payments.export.reason")}</label>
          <input id="payment-export-reason" value={reason} onChange={(event) => setReason(event.target.value)} />
        </div>
        <button className="btn" type="button" onClick={() => void run()} disabled={pending}>
          {pending ? t("payments.export.running") : t("payments.export.button")}
        </button>
      </div>
      <p className="foot">{done ? t("payments.export.done") : t("payments.export.hint")}</p>
      {error ? <ErrorNotice error={error} /> : null}
    </div>
  );
}
